/**
 * Barber list view component (table-like)
 */
import { Barber } from '@/types';

interface BarberListViewProps {
  barbers: Barber[];
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onToggleAvailability: (id: string, isAvailable: boolean) => void;
  showEmail?: boolean;
}

export function BarberListView({ barbers, onEdit, onDelete, onToggleAvailability, showEmail = false }: BarberListViewProps) {
  return (
    <div className="bg-surface shadow-md rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-hairline">
          <thead className="bg-surface-2">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                Photo
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                Name
              </th>
              {showEmail && (
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                  Email
                </th>
              )}
              <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                Specialties
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                Availability
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-ink-subtle uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-surface divide-y divide-hairline">
            {barbers.map((barber) => (
              <tr key={barber.barberId} className="hover:bg-surface-2 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  {barber.photoURL ? (
                    <img
                      src={barber.photoURL}
                      alt={barber.displayName}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold">
                      {barber.displayName.charAt(0).toUpperCase()}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-ink">{barber.displayName}</div>
                  {barber.bio && (
                    <div className="text-sm text-ink-subtle truncate max-w-xs">{barber.bio}</div>
                  )}
                </td>
                {showEmail && (
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-ink-muted">
                      {barber.email || '-'}
                    </div>
                  </td>
                )}
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-1">
                    {barber.specialties.map((specialty, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 text-xs bg-info/10 text-info rounded-full"
                      >
                        {specialty}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => onToggleAvailability(barber.barberId, !barber.isAvailable)}
                    className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${
                      barber.isAvailable
                        ? 'bg-success/10 text-success hover:bg-success/20'
                        : 'bg-surface-2 text-ink hover:bg-surface-3'
                    }`}
                  >
                    {barber.isAvailable ? 'Available' : 'Unavailable'}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    barber.isActive
                      ? 'bg-success/10 text-success'
                      : 'bg-danger/10 text-danger'
                  }`}>
                    {barber.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => onEdit(barber.barberId)}
                    className="text-info hover:text-info mr-4"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => onDelete(barber.barberId)}
                    className="text-danger hover:text-danger"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
